import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import {
  FaSearchPlus,
  FaDownload,
  FaTimes,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import "../App.css";

// =========================================================================
// 📸 IMPORT GALLERY IMAGES (GalleryImg1 to GalleryImg8.webp):
// =========================================================================
import ManufacturingUnit from "../image/ManufacturingUnit.webp";
import GalleryImg1 from "../image/GalleryImg1.webp";
import GalleryImg2 from "../image/GalleryImg2.webp";
import GalleryImg3 from "../image/GalleryImg3.webp";
import GalleryImg4 from "../image/GalleryImg4.webp";
import GalleryImg5 from "../image/GalleryImg5.webp";
import GalleryImg6 from "../image/GalleryImg6.webp";
import GalleryImg7 from "../image/GalleryImg7.webp";
import GalleryImg8 from "../image/GalleryImg8.webp";

// Infrastructure Gallery List (8 Images rendered in 4-column 1:1 square grid):
const infrastructureGallery = [
  { id: 1, title: "Infrastructure Showcase 1", image: GalleryImg1 },
  { id: 2, title: "Infrastructure Showcase 2", image: GalleryImg2 },
  { id: 3, title: "Infrastructure Showcase 3", image: GalleryImg3 },
  { id: 4, title: "Infrastructure Showcase 4", image: GalleryImg4 },
  { id: 5, title: "Infrastructure Showcase 5", image: GalleryImg5 },
  { id: 6, title: "Infrastructure Showcase 6", image: GalleryImg6 },
  { id: 7, title: "Infrastructure Showcase 7", image: GalleryImg7 },
  { id: 8, title: "Infrastructure Showcase 8", image: GalleryImg8 },
];

// Interactive Zoomable Image for Modal
const ZoomableImage = ({ src, alt }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [position, setPosition] = useState({ x: "50%", y: "50%" });

  const handleMouseMove = (e) => {
    if (!isZoomed) return;
    const { left, top, width, height } = e.target.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setPosition({ x: `${x}%`, y: `${y}%` });
  };

  const handleClick = () => {
    setIsZoomed(!isZoomed);
    if (isZoomed) {
      setPosition({ x: "50%", y: "50%" });
    }
  };

  return (
    <div
      className="zoom-container"
      style={{
        overflow: "hidden",
        cursor: isZoomed ? "zoom-out" : "zoom-in",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      <img
        src={src}
        alt={alt}
        className="gallery-main-img-dark"
        style={{
          transform: isZoomed ? "scale(2.2)" : "scale(1)",
          transformOrigin: `${position.x} ${position.y}`,
          transition: "transform 0.2s ease-out",
          width: "100%",
          height: "100%",
          maxHeight: "85vh",
          objectFit: "contain"
        }}
      />
    </div>
  );
};

export default function InfrastructurePage() {
  const [selectedIdx, setSelectedIdx] = useState(null);

  const handlePrevImage = (e) => {
    if (e) e.stopPropagation();
    setSelectedIdx((prev) => (prev === 0 ? infrastructureGallery.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    if (e) e.stopPropagation();
    setSelectedIdx((prev) => (prev === infrastructureGallery.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="page-wrapper light-section min-vh-100">
      {/* Hero Banner Section */}
      <div className="page-banner position-relative">
        <div className="glowing-orb primary top-50 start-50 translate-middle" style={{ width: '400px', height: '400px', opacity: 0.5 }}></div>
        <div className="container position-relative z-1 text-center">
          <h1 className="page-banner-title text-white">Our Infrastructure</h1>
          <div className="page-banner-breadcrumb text-light opacity-75">
            <Link to="/" className="text-light text-decoration-none">Home</Link> <span>/</span> Our Infrastructure
          </div>
        </div>
      </div>

      {/* Main Info Section */}
      <section className="section-padding light-section">
        <div className="container position-relative z-1">
          <div className="row align-items-center flex-lg-row-reverse mb-0">
            <div className="col-lg-6 mb-4 mb-lg-0 px-3 px-md-4 px-lg-4">
              <div className="about-image-wrapper rounded-4 shadow-lg overflow-hidden position-relative">
                <img src={ManufacturingUnit} alt="Infrastructure Unit" className="img-fluid w-100 h-100 transition-all hover-lift" style={{ objectFit: 'cover', minHeight: '320px' }} />
              </div>
            </div>
            <div className="col-lg-6 px-3 px-md-4 px-lg-4 mt-2 mt-lg-0">
              <span className="section-pill mb-3">State-of-the-art</span>
              <h2 className="section-heading text-dark mb-3 mb-md-4 fs-3 fs-md-2">Manufacturing <span className="text-primary fst-italic">Unit</span></h2>

              <div className="white-card p-3 p-md-4 p-lg-5 rounded-4 shadow-sm border-0">
                <p className="text-muted-dark mb-3 mb-md-4 fs-6" style={{ lineHeight: '1.8' }}>
                  Located in Surat (Gujarat, India), we are supported by a well-functional and highly robust infrastructural unit that assists us in the manufacturing of a wide range of products as per the set industry norms.
                </p>
                <p className="text-muted-dark mb-3 mb-md-4 fs-6" style={{ lineHeight: '1.8' }}>
                  Our infrastructure is carefully segregated into various departments including manufacturing, quality testing, warehousing, and packaging. This allows for a smooth, streamlined workflow and ensures every product that leaves our facility meets our stringent quality standards.
                </p>
                <ul className="list-unstyled mt-3 mt-md-4 mb-0 text-muted-dark">
                  <li className="mb-2 mb-md-3 d-flex align-items-center"><span className="text-primary me-2 fs-5">✓</span> Advanced Machinery & Tools</li>
                  <li className="mb-2 mb-md-3 d-flex align-items-center"><span className="text-primary me-2 fs-5">✓</span> Experienced Technical Workforce</li>
                  <li className="mb-2 mb-md-3 d-flex align-items-center"><span className="text-primary me-2 fs-5">✓</span> Dedicated Quality Control Division</li>
                  <li className="mb-2 mb-md-0 d-flex align-items-center"><span className="text-primary me-2 fs-5">✓</span> Spacious Warehousing</li>
                </ul>
                <div className="mt-4 mt-md-5">
                  <Link to="/collections" className="btn btn-primary rounded-pill px-4 py-2 hover-lift">VIEW OUR PRODUCTS</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure & Product Gallery Section (Square 1:1 Aspect Ratio Grid) */}
      <section className="section-padding dark-section" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="container position-relative z-1">
          <div className="text-center mb-4 mb-md-5">
            <span className="section-pill mb-3">Facility & Products</span>
            <h2 className="section-heading text-white fs-3 fs-md-2">
              Beds, Cupboards & <span className="text-primary fst-italic">Infrastructure Gallery</span>
            </h2>
            <p className="text-light opacity-75 mx-auto px-2 px-md-0" style={{ maxWidth: '600px' }}>
              Explore our high-grade steel beds, laser cut cupboards, and manufacturing facilities in high definition.
            </p>
          </div>

          {/* 4-Column 1:1 Square Grid */}
          <div className="row row-cols-2 row-cols-md-4 g-3 g-md-4">
            {infrastructureGallery.map((item, index) => (
              <div key={item.id} className="col">
                <div
                  className="infra-gallery-card position-relative rounded-4 overflow-hidden shadow-sm w-100 bg-dark"
                  style={{ aspectRatio: '1 / 1' }}
                  onClick={() => setSelectedIdx(index)}
                  title="Click to expand view"
                >
                  <img
                    src={item.image}
                    alt="Infrastructure Showcase"
                    className="w-100 h-100 transition-all"
                    style={{ objectFit: 'cover' }}
                  />

                  {/* Expand Icon Hover Overlay (No Text) */}
                  <div className="position-absolute top-50 start-50 translate-middle z-2">
                    <div className="infra-expand-icon">
                      <FaSearchPlus size={20} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Modal - Matching Product Details Dark Fullscreen UI with Zoom */}
      <Modal
        show={selectedIdx !== null}
        onHide={() => setSelectedIdx(null)}
        centered
        className="gallery-modal-dark"
        backdropClassName="gallery-backdrop-dark"
        fullscreen={true}
      >
        <Modal.Body className="p-0 gallery-modal-body-dark">
          {selectedIdx !== null && (
            <>
              {/* Header: Title & Close Actions */}
              <div className="gallery-header">
                <div className="gallery-header-left">
                  <h5 className="gallery-title">
                    {infrastructureGallery[selectedIdx].title}
                  </h5>
                  <span className="gallery-subtitle">Click to zoom image</span>
                </div>
                <div className="gallery-header-right">
                  {/* Download Button */}
                  <a
                    href={infrastructureGallery[selectedIdx].image}
                    download={`Infrastructure-Gallery-${selectedIdx + 1}.webp`}
                    className="action-btn-dark"
                    title="Download"
                  >
                    <FaDownload />
                  </a>
                  <button
                    onClick={() => setSelectedIdx(null)}
                    className="gallery-close-btn-dark"
                    title="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="gallery-main-view-dark">
                {/* Navigation - Left */}
                <button onClick={handlePrevImage} className="mobile-nav-btn prev" title="Previous Image">
                  <FaChevronLeft />
                </button>

                {/* Image Container with Zoom */}
                <div className="gallery-image-wrapper">
                  <ZoomableImage
                    src={infrastructureGallery[selectedIdx].image}
                    alt={infrastructureGallery[selectedIdx].title}
                  />
                </div>

                {/* Navigation - Right */}
                <button onClick={handleNextImage} className="mobile-nav-btn next" title="Next Image">
                  <FaChevronRight />
                </button>
              </div>

              {/* Bottom Floating Thumbnail Strip */}
              <div className="gallery-thumbs-track-dark">
                {infrastructureGallery.map((item, i) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedIdx(i)}
                    className={`gallery-modal-thumb-dark ${i === selectedIdx ? 'active' : ''}`}
                  >
                    <img src={item.image} alt={`Thumb ${i + 1}`} />
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
